import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card } from '../components';
import { Star, Building, X, ArrowLeft, Briefcase, ShieldCheck } from 'lucide-react';
import api from '../services/api';
import type { OpdrachtgeverReviewOverzicht } from '../services/reviewsApi';
import { useTranslation } from 'react-i18next';

type ProfessionalReviewsPageProps = { embedded?: boolean };

export const ProfessionalReviewsPage: React.FC<ProfessionalReviewsPageProps> = ({ embedded = false }) => {
  const { t } = useTranslation('professional');
  const [reviewModalEmployer, setReviewModalEmployer] = useState<{
    employer_id?: string;
    employer_name: string;
    average_rating: number;
    review_count: number;
    questions: Array<{ label: string; question: string; avg: number }>;
    open_comments: Array<{
      open_comment: string;
      submitted_at: string;
      employer_name?: string;
    }>;
  } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['professional-reviews'],
    queryFn: async () => {
      const response = await api.get('/professional/reviews');
      return response.data.data || { by_employer: [], total_average_rating: null, total_reviews: 0 };
    },
  });

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <p className="text-red-600">{t('reviews.loadError')}</p>
          <Link to="/professional/cockpit" className="mt-4 inline-block text-blue-600 hover:underline">
            {t('reviews.backToCockpit')}
          </Link>
        </div>
      </div>
    );
  }

  const byEmployer = data?.by_employer || [];
  const totalAverage = data?.total_average_rating ?? null;
  const totalReviews = data?.total_reviews ?? 0;

  return (
    <div className={embedded ? '' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'}>
      <div className="mb-8">
        {!embedded && (
          <Link
            to="/professional/cockpit"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('reviews.backToCockpit')}
          </Link>
        )}
        <h1 className="text-2xl font-bold text-gray-900">{t('reviews.title')}</h1>
        <p className="mt-1 text-gray-600">
          {t('reviews.subtitle')}
        </p>
      </div>

      <>
      {/* Total stats - clickable */}
      <Card className="mb-8 p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-sm font-medium text-gray-500 mb-1">{t('reviews.totalAverage')}</h2>
            <button
              type="button"
              onClick={() => {
                if (totalReviews > 0) {
                  setReviewModalEmployer({
                    employer_name: t('reviews.totalOverview'),
                    average_rating: totalAverage ?? 0,
                    review_count: totalReviews,
                    questions: data?.total_questions || [],
                    open_comments: data?.total_open_comments || []
                  });
                }
              }}
              className={`flex items-center gap-2 ${
                totalReviews > 0
                  ? 'cursor-pointer hover:opacity-80 transition-opacity'
                  : 'cursor-default'
              }`}
            >
              <Star className="w-8 h-8 fill-amber-500 text-amber-500" />
              <span className="text-3xl font-bold text-gray-900">
                {totalReviews > 0 && totalAverage != null ? totalAverage.toFixed(1) : '-'}
              </span>
              <span className="text-sm text-gray-500">
                {totalReviews > 0
                  ? t('reviews.basedOn', { count: totalReviews })
                  : t('reviews.noReviewsYet')}
              </span>
            </button>
          </div>
          {totalReviews > 0 && (
            <p className="text-sm text-gray-500">
              {t('reviews.clickForBreakdown')}
            </p>
          )}
        </div>
      </Card>

      <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-green-600" aria-hidden="true" />
        {t('reviews.medewerkerSection')}
      </h2>
      {/* List per employer */}
      {byEmployer.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {byEmployer.map((employer: any) => (
            <Card key={employer.employer_id} className="hover:shadow-lg transition-shadow">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                    <Building className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 truncate">
                      {employer.employer_name}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {t('reviews.reviewCount', { count: employer.review_count })}
                    </p>
                    {/* AVG-drempel van 3: pas vanaf 3 evaluaties ziet HR de scores */}
                    {employer.visible_to_employer === false ? (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {t('reviews.hiddenForEmployer', {
                          count: employer.reviews_needed_for_employer ?? 0,
                          defaultValue: 'Nog niet zichtbaar voor HR ({{count}} te gaan)'
                        })}
                      </p>
                    ) : (
                      <p className="text-xs text-green-700 mt-0.5">
                        {t('reviews.visibleForEmployer', { defaultValue: 'Zichtbaar voor HR' })}
                      </p>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    setReviewModalEmployer({
                      employer_id: employer.employer_id,
                      employer_name: employer.employer_name,
                      average_rating: employer.average_rating,
                      review_count: employer.review_count,
                      questions: employer.questions || [],
                      open_comments: employer.open_comments || []
                    })
                  }
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 text-amber-800 hover:bg-amber-100 text-sm font-medium w-full justify-center"
                >
                  <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
                  {employer.average_rating?.toFixed(1) ?? '-'} – {t('reviews.viewDetails')}
                </button>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <div className="text-center py-12">
            <Star className="mx-auto h-16 w-16 text-gray-300 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('reviews.noReviewsYet')}</h2>
            <p className="text-gray-600 mb-4">
              {t('reviews.noReviewsDesc')}
            </p>
            <Link
              to="/professional/cockpit"
              className="inline-flex items-center text-blue-600 hover:underline"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('reviews.backToCockpit')}
            </Link>
          </div>
        </Card>
      )}
      </>

      <OpdrachtgeverReviews
        data={data?.opdrachtgever as OpdrachtgeverReviewOverzicht | undefined}
        onOpen={(e) =>
          setReviewModalEmployer({
            employer_id: e.employer_id,
            employer_name: e.employer_name,
            average_rating: e.average_rating,
            review_count: e.review_count,
            questions: e.questions,
            open_comments: e.open_comments,
          })
        }
      />

      {/* Review detail modal */}
      {reviewModalEmployer && (
        <ReviewDetailModal
          data={reviewModalEmployer}
          onClose={() => setReviewModalEmployer(null)}
        />
      )}
    </div>
  );
};

// Modal for review detail (read-only)
const ReviewDetailModal: React.FC<{
  data: {
    employer_name: string;
    average_rating: number;
    review_count: number;
    questions: Array<{ label: string; question: string; avg: number }>;
    open_comments: Array<{ open_comment: string; submitted_at: string; employer_name?: string }>;
  };
  onClose: () => void;
}> = ({ data, onClose }) => {
  const { t } = useTranslation('professional');
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {t('reviews.reviewsFor', { name: data.employer_name })}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <>
              <div className="flex items-center gap-2 mb-6">
                <div className="flex items-center">
                  <Star className="w-6 h-6 fill-amber-500 text-amber-500" />
                  <span className="ml-1 text-xl font-semibold text-gray-900">
                    {data.average_rating.toFixed(1)}
                  </span>
                </div>
                <span className="text-sm text-gray-500">
                  {t('reviews.averageBasedOn', { count: data.review_count })}
                </span>
              </div>
              {data.questions.length > 0 && (
                <div className="space-y-4 mb-6">
                  <h4 className="text-sm font-medium text-gray-700">{t('reviews.breakdownByQuestion')}</h4>
                  {data.questions.map((q: any, i: number) => (
                    <div key={i} className="border-b border-gray-100 pb-3 last:border-0">
                      <p className="text-sm font-medium text-gray-800">{q.label}</p>
                      <p className="text-xs text-gray-600 mb-1">{q.question}</p>
                      <div className="flex items-center gap-2">
                        <div className="flex gap-0.5">
                          {[1, 2, 3, 4, 5].map((v) => (
                            <Star
                              key={v}
                              className={`w-4 h-4 ${
                                q.avg >= v ? 'fill-amber-400 text-amber-400' : 'text-gray-200'
                              }`}
                            />
                          ))}
                        </div>
                        <span className="text-sm text-gray-600">
                          {q.avg > 0 ? q.avg.toFixed(1) : '-'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {data.open_comments.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700">
                    {t('reviews.personalComments')}
                  </h4>
                  {data.open_comments.map((oc: any, i: number) => (
                    <div
                      key={i}
                      className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 border border-gray-100"
                    >
                      <p className="whitespace-pre-wrap">{oc.open_comment}</p>
                      <p className="text-xs text-gray-500 mt-2">
                        {new Date(oc.submitted_at).toLocaleDateString('nl-NL')}
                        {oc.employer_name && <> • {oc.employer_name}</>}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </>
        </div>
      </div>
    </div>
  );
};

/** B2B-projectevaluaties: HR/directie beoordeelt de professional per afgeronde mijlpaal. */
const OpdrachtgeverReviews: React.FC<{
  data: OpdrachtgeverReviewOverzicht | undefined;
  onOpen: (e: OpdrachtgeverReviewOverzicht['by_employer'][number]) => void;
}> = ({ data, onOpen }) => {
  const { t } = useTranslation('professional');
  const lijst = data?.by_employer ?? [];
  return (
    <section className="mt-10">
      <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
        <Briefcase className="w-5 h-5 text-[#0B1F3A]" aria-hidden="true" />
        {t('reviews.opdrachtgeverSection')}
      </h2>
      <p className="text-sm text-gray-600 mb-4">{t('reviews.opdrachtgeverDesc')}</p>
      {lijst.length === 0 ? (
        <Card className="p-6">
          <p className="text-sm text-gray-500">{t('reviews.noOpdrachtgeverReviews')}</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {lijst.map((e) => (
            <Card key={e.employer_id} className="p-6">
              <div className="flex items-center mb-3">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mr-3 shrink-0">
                  <Building className="w-6 h-6 text-blue-600" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">{e.employer_name}</h3>
                  <p className="text-sm text-gray-500">{t('reviews.reviewCount', { count: e.review_count })}</p>
                </div>
              </div>
              <ul className="space-y-1 mb-4 text-sm text-gray-700">
                {e.mijlpalen.slice(0, 4).map((m) => (
                  <li key={m.id} className="flex justify-between gap-2">
                    <span className="truncate">
                      {m.project_code ? `${m.project_code} · ` : ''}
                      {m.milestone_volgnummer != null ? `M${m.milestone_volgnummer} ` : ''}
                      {m.milestone_titel ?? m.project_titel ?? ''}
                    </span>
                    <span className="inline-flex items-center gap-0.5 shrink-0">
                      <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" aria-hidden="true" />
                      {m.average_rating.toFixed(1)}
                    </span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => onOpen(e)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 text-amber-800 hover:bg-amber-100 text-sm font-medium w-full justify-center"
              >
                <Star className="w-4 h-4 fill-amber-500 text-amber-500" aria-hidden="true" />
                {e.average_rating.toFixed(1)} – {t('reviews.viewDetails')}
              </button>
            </Card>
          ))}
        </div>
      )}
    </section>
  );
};
